import { ChangeDetectionStrategy, Component, EventEmitter, Output } from '@angular/core';
import { Router } from '@angular/router';
import { SeatInfo, SeatInfoPassengers, SeatNumbers } from '@common/interfaces/servicing-seatmap/seat-info';
import {
  ChildSeatInfo,
  PassengerApplicationStatusMap,
  SeatReservationStatus,
  SegmentMap,
} from '@common/interfaces/servicing-seatmap/seat-reservation-status';
import { PassengerForServicingSeatmapScreen } from '@common/interfaces/servicing-seatmap/passenger-for-seatmap-screen';
import { SeatForServicingSeatmapScreen } from '@common/interfaces/servicing-seatmap/seatmap-for-seatmap-screen';
import { SERVICING_ROUTES } from '@conf/routes.config';
import { SupportModalBlockComponent } from '@lib/components/support-class';
import {
  AlertMessageItem,
  AlertType,
  ApiErrorResponseModel,
  DialogClickType,
  DialogInfo,
  DialogSize,
  DialogType,
  PageType,
  RetryableError,
  ErrorType,
} from '@lib/interfaces';
import {
  CreateMycarValetResponseWarningsInner,
  GetOrderResponseData,
  Type1,
  UpdateServicesRequest,
  UpdateServicesRequestSeats,
  UpdateServicesRequestSeatsChildSeatsInner,
  UpdateServicesRequestSeatsSeatReservationInner,
  UpdateServicesResponse,
  DefaultService as ServicingApiService,
  UpdateServicesRequestSeatsSeatReservationInnerSeatSelectionInner,
  UpdateServicesRequestSeatsSeatReservationInnerAttributeRequest,
  UpdateServicesRequestServicesInner,
  UpdateServicesRequestServicesInnerSegmentsInnerTravelersInnerSpecialServiceRequestsInner,
  OrderSeatItemSeatSelectionSeatCharacteristicsInner,
} from 'src/sdk-servicing';
import { ServicingSeatmapStoreService } from '@common/services/seatmap/servicing-seatmap-store.service';
import { ServicingCommonStoreService } from '@common/services/servicing-common-store/servicing-common-store.service';
import { AswServiceStoreService, CommonLibService, DialogDisplayService, PageLoadingService } from '@lib/services';
import { ChildSeats } from '@common/interfaces/servicing-seatmap/child-seats';
import { take, zip } from 'rxjs';
import { MasterJsonKeyPrefix, MasterStoreKey } from '@conf/asw-master.config';
import { ListData } from '@common/interfaces/mycar-valet-request/list-data';
import { splitCouchSeatNumber } from '@common/helper';
import { ServicingCommonModel, ServicingFunctionIdType } from '@common/interfaces';
import { ServicingSeatmapService } from '@common/services/seatmap/servicing-seatmap.service';
import { ServicingSeatmapBackupStoreService } from '@common/services/seatmap/servicing-seatmap-backup-store.service';
import { ErrorCodeConstants } from '@conf/app.constants';

@Component({
  selector: 'asw-servicing-seatmap-confirm-seat-reservation',
  templateUrl: './servicing-seatmap-confirm-seat-reservation.component.html',
  styleUrls: ['./servicing-seatmap-confirm-seat-reservation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServicingSeatmapConfirmSeatReservationComponent extends SupportModalBlockComponent {
  /** 搭乗者マップ */
  travelersMap?: Map<string, PassengerForServicingSeatmapScreen>;

  /** 搭乗者IDリスト */
  passengerIdList: string[] = [];

  /** 全セグメント情報 */
  allSegmentInfo?: Array<Type1>;

  /** 表示対象セグメントID */
  displayTargetSegmentId?: string;

  /** 選択中座席情報リスト */
  selectedSeatInfoList?: SeatInfo[];

  /** pnr情報 */
  pnrInfo?: GetOrderResponseData;

  /** 座席指定状況 */
  seatReservationStatus?: SeatReservationStatus;

  /** カウチ席情報 */
  couchSeatInfo: { flightId: string; seatNo: string }[] = [];

  /** 確認ボタン押下かどうか */
  isConfirmClick?: boolean;

  /** チャイルドシート申込リスト */
  childSeatApplicationList: ChildSeats[] | undefined;

  /** 内容確定・購入へボタンのクリックイベント */
  @Output() clickApplyService = new EventEmitter<void>();

  /** 他のサービス申し込むボタンのクリックイベント */
  @Output() clickSelectOtherServices = new EventEmitter<void>();

  /** 確認ダイアログ表示用配列 */
  private _confirmDialogs: DialogInfo[] = [];

  /** 座席属性 */
  readonly seatAttributeWindow = 'NSSW';
  readonly seatAttributeAisle = 'NSSA';
  private _seatAttributeDisplays = new Map<string, string>();

  readonly initDisplayService = 'seatRequest';
  readonly tabShow = 'service';

  /* 事前座席指定リスト */
  private _seatReservation: Array<UpdateServicesRequestSeatsSeatReservationInner> = [];

  /* セグメントスキップエラーメッセージID(他画面へ遷移する際に使用) */
  private _skipSegmentErrorMessageId: string = '';

  //* サービス情報登録APIのワーニングコードに該当するメッセージID */
  private readonly WARNING_MAPPING: Record<string, string> = {
    [ErrorCodeConstants.ERROR_CODES.WBAZ000253]: 'W0444',
    [ErrorCodeConstants.ERROR_CODES.WBAZ000254]: 'W0444',
    [ErrorCodeConstants.ERROR_CODES.WBAZ000257]: 'W0444',
    [ErrorCodeConstants.ERROR_CODES.WBAZ000213]: 'W0444',
    [ErrorCodeConstants.ERROR_CODES.WBAZ000214]: 'W0444',
    [ErrorCodeConstants.ERROR_CODES.WBAZ000355]: 'W0444',
    [ErrorCodeConstants.ERROR_CODES.WBAZ000389]: 'W0426',
    [ErrorCodeConstants.ERROR_CODES.WBAZ000218]: 'W0742',
    [ErrorCodeConstants.ERROR_CODES.WBAZ000219]: 'W0814',
  };
  //* サービス情報登録APIのエラーコードに該当するメッセージID */
  private readonly ERROR_MAPPING: Record<string, string> = {
    [ErrorCodeConstants.ERROR_CODES.EBAZ000252]: 'E0710',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000822]: 'E1828',
    [ErrorCodeConstants.ERROR_CODES.EBAZ000262]: 'E1814',
  };

  constructor(
    private _commonLibService: CommonLibService,
    private _servicingSeatmapService: ServicingSeatmapStoreService,
    private _servicingCommonService: ServicingCommonStoreService,
    private _dialogDisplayService: DialogDisplayService,
    private _router: Router,
    private _aswServiceStoreService: AswServiceStoreService,
    private _servicingApiService: ServicingApiService,
    private _loadingSvc: PageLoadingService,
    private _seatmapService: ServicingSeatmapService,
    private _servicingSeatmapBackupStoreService: ServicingSeatmapBackupStoreService
  ) {
    super(_commonLibService);
  }

  override reload(): void {}

  override init(): void {
    this.subscribeService(
      'ServicingSeatmapConfirmSeatReservationModalComponent-init',
      zip(
        this._servicingSeatmapService.getServicingSeatmapModel$(),
        this._commonLibService.aswMasterService.getAswMasterByKey$(MasterStoreKey.LISTDATA_ALL).pipe(take(1))
      ),
      ([state, listDatas]) => {
        if (state && state.passengers && this._servicingCommonService.servicingCommon.displayTargetSegmentId) {
          this.travelersMap = new Map<string, PassengerForServicingSeatmapScreen>(Object.entries(state.passengers));
          this.travelersMap.forEach((value, key) => {
            this.passengerIdList?.push(key);
          });
          this.allSegmentInfo = state.segments;
          this.displayTargetSegmentId = this._servicingCommonService.servicingCommon.displayTargetSegmentId;
          this.selectedSeatInfoList = state.selectedSeatInfoList;
          this.pnrInfo = state.pnrInfo;
          this.isConfirmClick = state.isConfirmClick;
          this.childSeatApplicationList = state.childSeatAppliedList;
        }
        if (!!listDatas) {
          const listDataWindow =
            listDatas.find(
              (data: ListData) =>
                data.data_code === 'PD_035' &&
                data.lang === this._commonLibService.aswContextStoreService.aswContextData.lang &&
                data.value === 'NSSW'
            )?.display_content || '';
          this._seatAttributeDisplays.set(this.seatAttributeWindow, listDataWindow);

          const listDataAisle =
            listDatas.find(
              (data: ListData) =>
                data.data_code === 'PD_035' &&
                data.lang === this._commonLibService.aswContextStoreService.aswContextData.lang &&
                data.value === 'NSSA'
            )?.display_content || '';
          this._seatAttributeDisplays.set(this.seatAttributeAisle, listDataAisle);
        }
      }
    );

    // セグメントスキップエラーメッセージIDを取得しストアをクリアする。
    this._skipSegmentErrorMessageId = this._servicingSeatmapService.servicingSeatmap.skipSegmentErrorMessageId || '';
    this._servicingSeatmapService.updateServicingSeatmap({
      skipSegmentErrorMessageId: undefined,
    });

    this._handleUpdateServicesResponse = this._handleUpdateServicesResponse.bind(this);
    this._handleUpdateServicesError = this._handleUpdateServicesError.bind(this);
  }

  override destroy(): void {}

  /**
   * 内容確定・購入へボタンのクリックイベント
   */
  onClickApplyService() {
    // 以下の確認ダイアログ表示処理を行う。
    if (this.showLastSegmentConfirmationDialog(true)) {
      return;
    } else {
      this.setReservationInformation();
    }
  }

  /**
   * 内容確定・購入へボタン押下処理
   */
  setReservationInformation() {
    // 2 座席指定状況の設定を行う。
    this._setSeatReservationStatus();

    // 3 すべてのセグメントで搭乗者別申込状況マップの要素数が0、
    //   チャイルドシート情報リストの要素数が0であった場合、以下をパラメータに確認ダイアログ(G02-005)を表示する。
    // ”MSG1660”(選択内容を確認する旨)
    if (this._isEmptySeatReservationStatus() && this._isEmptyChildSeatInfoList()) {
      this._confirmDialogs.push({
        size: DialogSize.L,
        type: DialogType.CHOICE,
        message: `${MasterJsonKeyPrefix.DYNAMIC}MSG1660`,
      });
      this._showConfirmAgreement();
      return;
    }

    // 4  支払金額=0かつ他の有料サービスを選択していない場合、以下の処理を行う。
    if (this.seatReservationStatus!.totalAmount === 0 && !this.isOtherPaidServices()) {
      this.moveToOrderDetailPage();
    }
    // 5 上記以外の場合、以下パラメータを引き渡しAncillary支払情報入力画面(S02-P020)に遷移する。
    else {
      this.close();
    }
  }

  /**
   * 座席指定状況の空判定
   */
  private _isEmptySeatReservationStatus(): boolean {
    // 判定に必要な項目（選択中座席情報リスト）が存在しない場合、空として扱う
    if (!this.selectedSeatInfoList || this.selectedSeatInfoList?.length === 0) {
      return true;
    }

    // 「座席指定状況の搭乗者別申し込み状況（seatReservationStatus.[segmentId].[travelerId]）」が1つでも存在すれば、空ではない扱い
    // セグメントIDを指定して探す必要があるので、「選択中座席情報リスト（selectedSeatInfoList）」を使う
    for (let segment of this.selectedSeatInfoList) {
      // 判定に必要な項目（搭乗者リスト）が存在しない場合、対象外
      if (!segment.passengerList || segment.passengerList.length === 0) {
        continue;
      }
      // 空のオブジェクトが設定されている場合、対象外
      const segmentMap = this.seatReservationStatus?.[segment.segmentId] as SegmentMap;
      if (this._isEmptyObject(segmentMap)) {
        continue;
      }
      // 搭乗者IDを指定して探す必要があるので、「搭乗者リスト（passengerList）」を使う
      for (let passenger of segment.passengerList) {
        // セグメント・搭乗者の単位で保持している変更差分を取得する
        const applicationStatusMap = segmentMap?.[passenger.id] as PassengerApplicationStatusMap;
        if (!this._isEmptyObject(applicationStatusMap)) {
          // 変更差分が1つでも存在すれば、その時点で座席指定状況は空ではないものとして扱う
          return false;
        }
      }
    }

    // 上記以外の場合、座席指定状況を空と判別する。
    return true;
  }

  /**
   * チャイルドシート情報リストの空(未変更)判定
   * @returns true: チャイルドシート情報リスト空(未変更)、false: それ以外
   */
  private _isEmptyChildSeatInfoList(): boolean {
    // チャイルドシート申込が存在しない場合、空(未変更)として扱う
    if (!this.seatReservationStatus?.childSeatInfoList || this.seatReservationStatus?.childSeatInfoList?.length === 0) {
      return true;
    }

    return false;
  }

  /**
   * オブジェクトが空か判定
   * @param obj 判定対象のオブジェクト
   * @returns
   */
  private _isEmptyObject(obj: object) {
    // 値が設定されている場合
    if (!!obj && Object.keys(obj).length >= 0) {
      return false;
    }
    // 上記以外
    return true;
  }

  /**
   * 他の有料サービスを選択しているか判定
   */
  public isOtherPaidServices(): boolean {
    const chargeableLoungeIsApplicationProcessed =
      this._servicingCommonService.servicingCommon.chargeableLounge?.isApplicationProcessed || false;
    const chargeableLoungeTotalAmount = this._servicingCommonService.servicingCommon.chargeableLounge?.totalAmount || 0;
    const firstBaggageIsApplicationProcessed =
      this._servicingCommonService.servicingCommon.firstBaggage?.isApplicationProcessed || false;
    const firstBaggageTotalAmount = this._servicingCommonService.servicingCommon.firstBaggage?.totalAmount || 0;
    const mealIsApplicationProcessed =
      this._servicingCommonService.servicingCommon.meal?.isApplicationProcessed || false;
    const mealTotalAmount = this._servicingCommonService.servicingCommon.meal?.totalAmount || 0;

    // 以下のいずれかに該当する場合、他の有料サービスを選択しているか判定をtrueとする。
    // サービシング共通.有料ラウンジ(ChargeableLounge).申込操作中フラグ=true かつサービシング共通.有料ラウンジ(ChargeableLounge).合計金額 > 0
    // サービシング共通.事前追加手荷物(FirstBaggage).申込操作中フラグ=true かつサービシング共通.事前追加手荷物(FirstBaggage).合計金額 > 0
    // サービシング共通.機内食(meal).申込操作中フラグ=true かつサービシング共通.機内食(meal).合計金額 > 0
    if (
      (chargeableLoungeIsApplicationProcessed && chargeableLoungeTotalAmount > 0) ||
      (firstBaggageIsApplicationProcessed && firstBaggageTotalAmount > 0) ||
      (mealIsApplicationProcessed && mealTotalAmount > 0)
    ) {
      return true;
    }
    // 上記以外の場合、他の有料サービスを選択しているか判定をfalseとする。
    return false;
  }

  /**
   * 座席指定状況の設定
   */
  private _setSeatReservationStatus() {
    // API、遷移先画面へ渡すパラメータとして、以下を作成する。
    let segments: {
      [segmentId: string]: {
        [key2: string]: PassengerApplicationStatusMap | undefined;
      };
    } = {};

    // 座席指定状況変更フラグをfalseで用意する。
    let isSeatChange = false;

    this.pnrInfo?.air?.bounds?.forEach((bound) => {
      bound.flights?.forEach((flight) => {
        if (!!this.selectedSeatInfoList?.find((seatInfo) => seatInfo.segmentId === flight.id)) {
          this.selectedSeatInfoList
            ?.filter((s) => s.segmentId === flight.id)
            .forEach((traveler) => {
              let passengerApplicationStatusMap = new Map<string, PassengerApplicationStatusMap>();
              traveler.passengerList.forEach((passenger) => {
                // 対象座席情報に含まれる、対象搭乗者の座席が変更されている場合、座席指定状況を設定
                if (!!flight.id && this._isPassengerChangedSeat(traveler, passenger.id)) {
                  // 座席番号は、搭乗者毎に1席設定。カウチの場合は通路（左優先）側から順に設定
                  let seatNumber =
                    !!passenger.seatNumberList && passenger.seatNumberList.length > 0
                      ? passenger.seatNumberList[0].seatNumber
                      : '';

                  // カウチ座席番号が存在すれば設定。こちらは配列に複数設定されない前提とする。
                  const couchSeatNumber =
                    !!passenger.couchSeatNumberList && passenger.couchSeatNumberList.length > 0
                      ? passenger.couchSeatNumberList
                      : [];

                  let seatInformation = undefined;
                  if (!!passenger.seatNumberList) {
                    seatInformation = this._seatmapService.getSeatInformation(
                      flight.id,
                      passenger.seatNumberList[0]?.seatNumber,
                      this._servicingSeatmapService.servicingSeatmap
                    );
                  }

                  // カウチ席かどうか
                  const isCouchSeat =
                    !!passenger.seatNumberList && passenger.seatNumberList.length > 0 && !!seatInformation
                      ? seatInformation.isCouchSeat
                      : false;

                  // カウチ座席を選択している場合、着席順を補正して座席番号を設定する
                  if (isCouchSeat && couchSeatNumber.length > 0) {
                    const seatInformations = this._seatmapService.getSeatInformations(
                      flight.id,
                      this._servicingSeatmapService.servicingSeatmap
                    );
                    seatNumber = this._getAdjusttedSeatNumber(
                      couchSeatNumber,
                      passengerApplicationStatusMap,
                      seatInformations
                    );
                  }

                  // 座席特性
                  let seatCharacteristics: OrderSeatItemSeatSelectionSeatCharacteristicsInner[] = [];
                  if (
                    !!passenger.seatNumberList &&
                    passenger.seatNumberList.length > 0 &&
                    !!seatInformation &&
                    !!seatInformation.seatCharacteristicsCodes &&
                    seatInformation.seatCharacteristicsCodes.length > 0
                  ) {
                    seatInformation.seatCharacteristicsCodes?.forEach((code) => {
                      seatCharacteristics.push({ code: code });
                    });
                  }

                  // 座席属性の変更有無を判定
                  const isChangedSeatAttribute: boolean = this._seatmapService.isChangedSeatAttribute(traveler);

                  // 合計金額（表示内容と同等の条件で金額を取得。0やundefinedは空文字とする。）
                  const tmpPrice: number = this.getSeatPrice(passenger.id, flight.id);
                  const tmpPaidServiceAmount: string = !!tmpPrice ? tmpPrice.toString() : '';

                  // 更新区分・SSRコード
                  // ・「座席属性は未指定、かつ、カウチSSR未登録、かつ、カウチ座席選択中」の場合は「requestとして扱う」
                  // ・「座席属性は未指定、かつ、カウチSSR登録済み、かつ、選択中座席なし」の場合は「cancelとして扱う」
                  // ・「それ以外」の場合は「未設定として扱う」
                  let tmpUpdateCategory = '';
                  let tmpSsrCode = '';
                  const isRegisteredCounch = this._seatmapService.isRegisteredCounch(
                    flight.id,
                    passenger.id,
                    this._servicingSeatmapService.servicingSeatmap,
                    this.pnrInfo
                  );
                  if (!isChangedSeatAttribute && !!isRegisteredCounch && !seatNumber) {
                    // 「cancel」として扱う場合
                    tmpUpdateCategory = 'cancel';
                    tmpSsrCode = this._seatmapService.getSsrCodeFromRegisteredCouch(
                      flight.id,
                      passenger.id,
                      this._servicingSeatmapService.servicingSeatmap,
                      this.selectedSeatInfoList,
                      this.pnrInfo
                    );
                  } else if (!isChangedSeatAttribute && !isRegisteredCounch && isCouchSeat) {
                    // 「request」として扱う場合
                    tmpUpdateCategory = 'request';
                    tmpSsrCode = this._seatmapService.getSsrCodeFromSelectedCouch(
                      flight.id,
                      seatNumber,
                      this._servicingSeatmapService.servicingSeatmap,
                      this.selectedSeatInfoList
                    );
                  }

                  // 搭乗者別申し込み状況マップを設定
                  passengerApplicationStatusMap.set(passenger.id, {
                    seatNumber: isChangedSeatAttribute ? [{ seatNumber: '' }] : [{ seatNumber: seatNumber }],
                    couchSeatNumber: isChangedSeatAttribute ? [{ seatNumber: '' }] : couchSeatNumber,
                    seatAttribute: isChangedSeatAttribute ? traveler.characteristicRequestSsrCode : '',
                    paidServiceAmount: tmpPaidServiceAmount,
                    updateCategory: tmpUpdateCategory,
                    ssrCode: tmpSsrCode,
                    airBoundId: bound.airBoundId,
                    seatCharacteristics: isChangedSeatAttribute ? [] : seatCharacteristics,
                  });

                  // 座席指定状況変更フラグをtrue（変更有）に設定。
                  isSeatChange = true;
                }
              });
              if (!!flight.id && !!passengerApplicationStatusMap) {
                for (const [passengerId, passengerInfo] of passengerApplicationStatusMap ?? []) {
                  segments = {
                    ...segments,
                    [flight.id]: {
                      ...segments[flight.id],
                      [passengerId]: {
                        seatNumber: passengerInfo.seatNumber,
                        couchSeatNumber: passengerInfo.couchSeatNumber,
                        seatAttribute: passengerInfo.seatAttribute,
                        paidServiceAmount: passengerInfo.paidServiceAmount,
                        updateCategory: passengerInfo.updateCategory,
                        ssrCode: passengerInfo.ssrCode,
                        airBoundId: passengerInfo.airBoundId,
                        seatCharacteristics: passengerInfo.seatCharacteristics,
                      },
                    },
                  };
                }
              }
            });
        }
      });
    });

    // チャイルドシート情報設定
    let childSeatInfoList: ChildSeatInfo[] = [];
    this.passengerIdList?.forEach((passengerId) => {
      // チャイルドシート申込が存在する場合
      const childSeatApplied = this._servicingSeatmapService.servicingSeatmap.childSeatAppliedList?.find(
        (traveler) => traveler.passengerId === passengerId
      );

      // 申込済みのチャイルドシートを取得(全セグメント共通で設定されているため 表示対象セグメントID の値のみ取得)
      let previousChildSeatType = '';
      if (!!this.displayTargetSegmentId) {
        previousChildSeatType = this.pnrInfo?.seats?.[this.displayTargetSegmentId]?.[passengerId].childSeatType || '';
      }

      // チャイルドシート申込が変更されていた場合、チャイルドシート情報に追加
      if (!!childSeatApplied && previousChildSeatType !== childSeatApplied.childSeatType) {
        // 更新区分が"cancel"の場合、変更前のチャイルドシート種類を設定
        let updateChildSeatType = childSeatApplied.childSeatType;
        if (childSeatApplied.updateCategory === 'cancel') {
          updateChildSeatType = previousChildSeatType;
        }

        childSeatInfoList.push({
          travelerId: passengerId,
          childSeatType: updateChildSeatType,
          updateCategory: childSeatApplied.updateCategory,
        });

        // 座席指定状況変更フラグをtrue（変更有）に設定。
        isSeatChange = true;
      }
    });

    this.seatReservationStatus = {
      isApplicationProcessed: isSeatChange ? true : false,
      totalAmount: this.getTotalPrice(),
      ...segments,
      paidAsrCouchSeatDeletionFlag: this._getPaidAsrCouchSeatDeletionFlag(this.pnrInfo, this.selectedSeatInfoList),
      ...(childSeatInfoList.length > 0 && { childSeatInfoList: childSeatInfoList }),
    };
  }

  /**
   * 有料ASR席またはカウチ席削除フラグを取得
   * @param pnrInfo PNR情報
   * @param selectedSeatInfoList 選択中座席情報リスト
   */
  private _getPaidAsrCouchSeatDeletionFlag(pnrInfo?: GetOrderResponseData, selectedSeatInfoList?: SeatInfo[]) {
    let paidAsrCouchSeatDeletionFlag: boolean = false;
    selectedSeatInfoList?.forEach((seatInfo) => {
      seatInfo.passengerList.forEach((passenger) => {
        // 登録されている座席番号取得
        let pnrSeatNumber = undefined;
        let pnrCouchSeatNumber = undefined;
        const pnrSeatSelection = pnrInfo?.seats?.[seatInfo.segmentId]?.[passenger.id]?.seatSelection;
        if (!!pnrSeatSelection) {
          const pnrSeatNumbers = pnrSeatSelection.seatNumbers;
          pnrSeatNumber = !!pnrSeatNumbers && pnrSeatNumbers.length > 0 ? pnrSeatNumbers[0] : undefined;
          pnrCouchSeatNumber = !!pnrSeatSelection.couchSeatNumber ? pnrSeatSelection.couchSeatNumber : undefined;
        }

        // 選択中の座席番号取得
        let selectedSeatNumber = undefined;
        if (!!passenger.seatNumberList && passenger.seatNumberList.length > 0) {
          selectedSeatNumber = passenger.seatNumberList[0].seatNumber;
        }
        let selectedCouchSeatNumber = undefined;
        if (!!passenger.couchSeatNumberList && passenger.couchSeatNumberList.length > 0) {
          selectedCouchSeatNumber = passenger.couchSeatNumberList[0].seatNumber;
        }

        // カウチ席が取り消された場合
        if (!!pnrCouchSeatNumber && !selectedCouchSeatNumber) {
          paidAsrCouchSeatDeletionFlag = true;

          // カウチ席以外が取り消された場合
        } else if (!!pnrSeatNumber && !selectedSeatNumber) {
          // 取り消された座席が有料席の場合
          const seatInformation = this._seatmapService.getSeatInformation(
            seatInfo.segmentId,
            pnrSeatNumber,
            this._servicingSeatmapService.servicingSeatmap
          );
          if (!!seatInformation && seatInformation.isChargeableAsrSeat) {
            paidAsrCouchSeatDeletionFlag = true;
          }
        }
      });
    });

    return paidAsrCouchSeatDeletionFlag;
  }

  /**
   * 予約詳細（サービス）画面へ遷移する
   */
  moveToOrderDetailPage() {
    // 画面のローディング処理の開始
    this._loadingSvc.startLoading();

    // サービス情報を登録するため、変数を初期化する。

    // サービス申込情報リストを空で作成する。
    const services: UpdateServicesRequestServicesInner[] = [];

    // 座席指定情報を空で作成する。
    const seats: UpdateServicesRequestSeats = {};

    // 以下処理にて、座席指定サービス申し込み情報オブジェクトを作成する。
    // 事前座席指定リストを空で作成する。
    this._seatReservation = [];

    this.selectedSeatInfoList?.forEach((segment) => {
      const segmentId = segment.segmentId;
      let isTarget = false;
      let seatSelectionInfo: UpdateServicesRequestSeatsSeatReservationInnerSeatSelectionInner[] = [];
      let attributeRequestInfo: UpdateServicesRequestSeatsSeatReservationInnerAttributeRequest | undefined;

      // 座席指定状況.<当該セグメント情報.セグメントID>を取得し、セグメントマップ情報とする
      const segmentMap = this.seatReservationStatus?.[segmentId] as SegmentMap;
      // セグメントマップ情報が空の場合、以降の処理をスキップする。
      if (!segmentMap) {
        return;
      }

      // 代表者のみカウチSSRを指定するためのカウチ席番号を空で作成する。
      let registedCouchSeatNumber: string[] = [];

      // 座席属性指定フラグに false を設定する。
      let isSeatAttribute = false;

      segment?.passengerList?.forEach((passenger) => {
        const passengerId = passenger.id;
        const applicationStatusMap = segmentMap?.[passengerId] as PassengerApplicationStatusMap;

        // 対象のセグ・搭乗者に紐づく変更差分（サービス情報登録APIへ渡す内容）が無い場合は次のループへ進む
        if (!applicationStatusMap) return;

        // 当該シートマップ申込状況マップ.座席番号が存在する　または
        // 当該シートマップ申込状況マップ.カウチ席番号が存在する　または
        // 当該シートマップ申込状況マップ.座席属性が存在する または
        // 当該シートマップ申込状況マップ.SSR情報更新区分がcancelの場合、以下の処理を行う。
        const isSeatNumber = applicationStatusMap?.seatNumber && applicationStatusMap?.seatNumber.length > 0;
        const isCouchSeatNumber =
          applicationStatusMap?.couchSeatNumber &&
          applicationStatusMap.couchSeatNumber.length > 0 &&
          applicationStatusMap.couchSeatNumber[0].seatNumber;

        // 登録済みの座席番号を取得する。
        const registeredSeatNumbers = this.pnrInfo?.seats?.[segmentId]?.[passengerId].seatSelection.seatNumbers;
        const registeredSeatNumber =
          registeredSeatNumbers && registeredSeatNumbers.length > 0 ? registeredSeatNumbers[0] : '';
        const isSeatChanged = isSeatNumber
          ? registeredSeatNumber !== applicationStatusMap?.seatNumber![0].seatNumber
          : registeredSeatNumber
          ? true
          : false;

        if (
          isSeatChanged ||
          isCouchSeatNumber ||
          !!applicationStatusMap?.seatAttribute ||
          applicationStatusMap?.updateCategory === 'cancel'
        ) {
          // 座席情報
          let seatNumber = '';

          // シートマップ申込状況マップ.座席番号が存在する場合、シートマップ申込状況マップ.座席番号
          if (isSeatNumber) {
            seatNumber = applicationStatusMap?.seatNumber![0].seatNumber;
          }

          // シートマップ申込状況マップ.カウチ席番号が存在する場合、
          // またはシートマップ申込状況マップ.SSR情報更新区分がcancelの場合、サービス申込情報をセット
          if (isCouchSeatNumber || applicationStatusMap?.updateCategory === 'cancel') {
            // 登録時は、カウチ代表者(1人目)のみに対してSSRコードを指定したいため、
            // 今回既に同じSSR情報が登録されている場合はスキップ
            let isRegisterdSsrCode = false;
            if (
              isCouchSeatNumber &&
              registedCouchSeatNumber.includes(applicationStatusMap.couchSeatNumber![0].seatNumber)
            ) {
              isRegisterdSsrCode = true;
            }
            // サービス情報登録APIのリクエスト型に沿って値をセット
            if (
              !isRegisterdSsrCode &&
              !!applicationStatusMap?.airBoundId &&
              !!applicationStatusMap?.ssrCode &&
              !!applicationStatusMap?.updateCategory
            ) {
              // airBoundId === 当該バウンドIDとなるようなサービス申込情報がなければ作成
              let bound = services.filter((b) => b.airBoundId === applicationStatusMap?.airBoundId)[0];
              if (!bound) {
                services.push({
                  airBoundId: applicationStatusMap?.airBoundId,
                  segments: [],
                });
                bound = services.filter((b) => b.airBoundId === applicationStatusMap?.airBoundId)[0];
              }

              // segmentId === 当該セグメントIDとなるようなセグメント情報がなければ作成
              let segment = bound.segments.filter((s) => s.segmentId === segmentId)[0];
              if (!segment) {
                bound.segments.push({
                  segmentId: segmentId,
                  travelers: [],
                });
                segment = bound.segments.filter((s) => s.segmentId === segmentId)[0];
              }

              // travelerId === 当該搭乗者IDとなるような搭乗者情報がなければ作成
              let traveler = segment.travelers.filter((t) => t.travelerId === passengerId)[0];
              if (!traveler) {
                segment.travelers.push({
                  travelerId: passengerId,
                  specialServiceRequests: [],
                });
                traveler = segment.travelers.filter((t) => t.travelerId === passengerId)[0];
              }

              // SSR情報を指定
              traveler.specialServiceRequests.push({
                code: applicationStatusMap?.ssrCode,
                operation:
                  applicationStatusMap?.updateCategory as UpdateServicesRequestServicesInnerSegmentsInnerTravelersInnerSpecialServiceRequestsInner.OperationEnum,
              });

              // 登録時はカウチ代表者(1人目)のみに対してSSRコードを指定したいため、
              // 今回既に同じSSR情報が登録される場合はスキップのための処理
              if (isCouchSeatNumber && applicationStatusMap?.couchSeatNumber) {
                registedCouchSeatNumber.push(applicationStatusMap.couchSeatNumber[0].seatNumber);
              }
            }
          }

          if (!!seatNumber || !!registeredSeatNumber) {
            seatSelectionInfo.push({
              seatNumber: seatNumber,
              travelerId: passengerId,
            });
          }
          // 座席属性を設定
          let seatAttribute = applicationStatusMap?.seatAttribute;
          if (!!seatAttribute) {
            // 座席指定状況に設定されている時点で、座席属性に変更あり（新しく登録する）
            attributeRequestInfo = {
              type:
                seatAttribute === this.seatAttributeAisle
                  ? UpdateServicesRequestSeatsSeatReservationInnerAttributeRequest.TypeEnum.Nssa
                  : UpdateServicesRequestSeatsSeatReservationInnerAttributeRequest.TypeEnum.Nssw,
            };

            isSeatAttribute = true;
          }

          // 更新対象あり
          isTarget = true;
        }
      });
      if (isTarget) {
        if (!isSeatAttribute) {
          // 座席属性指定以外の場合
          this._seatReservation.push({
            flightId: segmentId,
            seatSelection: seatSelectionInfo,
          });
        } else {
          // 座席属性指定の場合
          this._seatReservation.push({
            flightId: segmentId,
            attributeRequest: attributeRequestInfo,
          });
        }
      }
    });

    // 事前座席指定情報リストが1件以上の場合、座席指定情報に以下の通り設定する。
    if (this._seatReservation.length > 0) {
      seats.seatReservation = this._seatReservation;
    }

    // チャイルドシート申込リストを空で作成する。
    const childSeats: Array<UpdateServicesRequestSeatsChildSeatsInner> = [];

    // チャイルドシート申込情報設定
    this.seatReservationStatus?.childSeatInfoList?.forEach((childSeatInfo: ChildSeatInfo) => {
      if (
        childSeatInfo.updateCategory === 'cancel' ||
        (!!childSeatInfo?.updateCategory && !!childSeatInfo?.childSeatType)
      ) {
        const operation =
          childSeatInfo.updateCategory === 'cancel'
            ? UpdateServicesRequestSeatsChildSeatsInner.OperationEnum.Cancel
            : UpdateServicesRequestSeatsChildSeatsInner.OperationEnum.Request;
        // 以下の通りにチャイルドシート申込リストを作成する。
        childSeats.push({
          travelerId: childSeatInfo.travelerId,
          operation: operation,
          type: childSeatInfo.childSeatType || '',
        });
      }
    });

    // チャイルドシート申込リストが1件以上の場合、座席指定情報に以下の通り設定する。
    if (childSeats.length > 0) {
      seats.childSeats = childSeats;
    }

    // 以下の通り設定し、サービス情報登録APIを呼び出す。
    const aswServiceData = this._aswServiceStoreService.aswServiceData;
    // リクエスト情報
    const requestInfo: UpdateServicesRequest = {
      orderId: aswServiceData.orderId,
      credential: {
        firstName: aswServiceData.firstName!,
        lastName: aswServiceData.lastName!,
      },
      services,
      seats,
    };

    // ローディング処理の終了はnext・errorの処理で実施する
    this._servicingApiService.ordersUpdateServicesPost(requestInfo).subscribe({
      next: this._handleUpdateServicesResponse,
      error: this._handleUpdateServicesError,
    });
  }

  /**
   * サービス情報登録APIが 正常終了の場合
   * @param response
   */
  private _handleUpdateServicesResponse(response: UpdateServicesResponse) {
    // 予約詳細(S01-P030)へ渡すパラメータとして、以下を作成する。
    this._servicingCommonService.updateServicingCommon({
      initDisplayService: this.initDisplayService,
      tabShow: this.tabShow,
      completedFunction: 'seatmap',
      dxApiRetrieveFlg: true,
    });

    // ワーニングメッセージリストを空で作成する。
    const warnings: Array<AlertMessageItem> = [];

    // 9.3.2.1.2. サービス情報登録API応答.warningsが1件以上の場合、以下の処理を行う。
    if (response.warnings?.length) {
      // ＜以下、サービス情報登録API応答.warningsの要素数分繰り返し＞
      response.warnings.forEach((warning) => {
        // 当該warning.codeは以下表のワーニングコードのいずれかが返却された場合、
        const warningCode = this.WARNING_MAPPING[warning.code]!;

        if (warningCode === 'W0444') {
          // ワーニングメッセージID="W0444"(座席指定の登録が失敗した旨)、埋め込み文字列置換用情報 = {0}: 販売キャリアコード＋便番号＋搭乗者名
          const mappedWarning = this._mapWarningSeatRegistrationFailed(warning, warningCode);
          // APIエラーコード = ワーニングコードというレコードをワーニングメッセージリストに追加する。
          warnings.push(mappedWarning);
        }

        if (warningCode === 'W0426' || warningCode === 'W0742' || warningCode === 'W0814') {
          // ワーニングメッセージID="W0426"(サービス申込の登録に失敗),"W0742"(登録済み機内食が削除できない),"W0814"(登録済みチャイルドシートが削除できない)、APIエラーコード = ワーニングコードをワーニングメッセージIDリストに追加する。
          warnings.push({
            contentHtml: warningCode,
            isCloseEnable: true,
            apiErrorCode: warning.code,
            errorMessageId: warningCode,
          });
        }
      });
      // ＜ここまで、サービス情報登録API応答.warningsの要素数分繰り返し＞
    }

    // 全サービスの編集情報破棄
    this._discardEditServiceInfo();

    // 座席指定セグメントリストの中に、NHグループ運航が含まれている場合
    if (this._isNhGroupOperated()) {
      // ワーニングメッセージIDに”W0420”(機材変更により座席が変更される可能性がある旨)をワーニングメッセージリストに追加する。全てのループ処理を中止とする。
      warnings.push({
        contentHtml: 'W0420',
        isCloseEnable: true,
        errorMessageId: 'W0420',
      });
    }

    // 予約詳細storeに以下の通りに設定する。
    this._servicingCommonService.updateServicingCommon({
      preWarningMessageList: warnings,
    });

    // セグメントスキップエラーメッセージIDが存在する場合、前画面からのワーニングメッセージリストに設定
    if (!!this._skipSegmentErrorMessageId) {
      let preWarningMessageList: Array<AlertMessageItem> = [];
      preWarningMessageList.push({
        contentHtml: this._skipSegmentErrorMessageId,
        isCloseEnable: true,
        alertType: AlertType.WARNING,
        errorMessageId: this._skipSegmentErrorMessageId,
      });
      this._servicingCommonService.updateServicingCommon({
        preWarningMessageList: preWarningMessageList,
      });
    }

    // 画面のローディング処理の終了
    this._loadingSvc.endLoading();

    // 予約詳細(S01-P030)へ遷移する。
    this.close();
    this._router.navigate([SERVICING_ROUTES.myBooking]);
  }

  /**
   * 全サービスの編集情報破棄
   */
  private _discardEditServiceInfo() {
    const servicingCommon: ServicingCommonModel = {
      ...this._servicingCommonService.servicingCommon,
      seatReservationStatus: undefined,
    };

    this._servicingCommonService.updateServicingCommon({
      ...servicingCommon,
    });
    this._servicingSeatmapService.setServicingSeatmap({});
    this._servicingSeatmapBackupStoreService.setServicingSeatmapBackup({});
  }

  /**
   * NHグループ運航が含まれるか判定
   * @returns
   */
  private _isNhGroupOperated() {
    let segmentInfo: Type1 | undefined;
    // 座席指定セグメントリストの中に、NHグループ運航が含まれるか判定する。
    if (this.allSegmentInfo) {
      // ＜以下、PNR情報.data.air.bounds[].flights[]の要素(以降、セグメント情報)数分繰り返し＞
      for (let segment of this.allSegmentInfo) {
        // 当該座席指定セグメント.セグメントID=当該セグメント情報.idとなるか判定する。
        if (this._seatReservation.find((val) => val.flightId === segment.id)) {
          // 一致する場合、一致した当該セグメント情報を対象セグメントとする。
          segmentInfo = segment;
          break;
        }
      }
      // ＜ここまで、セグメント情報の要素数分繰り返し＞
      // 対象セグメント.isNhGroupOperated=true(NH運航便)の場合
      if (segmentInfo?.isNhGroupOperated) {
        return true;
      }
    }

    return false;
  }

  /**
   * 座席指定の登録が失敗した場合
   * @param warning
   * @param warningCode
   * @returns
   */
  private _mapWarningSeatRegistrationFailed(
    warning: CreateMycarValetResponseWarningsInner,
    warningCode: string
  ): AlertMessageItem {
    // 埋め込み文字を空で作成する。
    let combined = '';

    // 当該warning.source.itemsを取得する。取得できた場合、以下プレースホルダの設定処理を行う。
    const warnItems = (warning.source as any)?.items;
    if (Array.isArray(warnItems)) {
      // 当該warning.source.itemsを繰り返し
      for (let seg of warnItems) {
        // 販売キャリアコードを空で作成する。
        let marketingAirlineCode = '';
        // 便番号を空で作成する。
        let marketingFlightNumber = '';
        // 搭乗者名を空で作成する。
        let travelerName = '';

        // 当該warning.source.items.flightIdを取得する。
        let flightId = seg.flightId;
        if (!!flightId) {
          // 当該warning.source.items.flightIdに紐づく全セグメント情報.セグメントIDを取得する。
          let findSegment = this.allSegmentInfo?.find((s) => s.id === flightId);
          // セグメント情報が取得できた場合、販売キャリアコードと便番号を設定する。
          if (!!findSegment) {
            marketingAirlineCode = findSegment.marketingAirlineCode ?? '';
            marketingFlightNumber = findSegment.marketingFlightNumber ?? '';
          }
          // 当該warning.source.items.travelerIdsを繰り返し
          seg.travelerIds?.forEach((travelerId: any) => {
            // 当該warning.source.items.travelerIdsに紐づくPNR情報.搭乗者情報（幼児含む）リスト.搭乗者IDを取得する。
            let findTraveler = this.pnrInfo?.travelers?.find((t) => t.id === travelerId);
            // 搭乗者情報が取得できた場合、搭乗者名を設定する。
            // 1人目の搭乗者情報の設定：タイトル＋搭乗者名＋搭乗者姓　例）MR TARO SORANO
            // 2人目以降の搭乗者情報の設定：搭乗者情報＋カンマ＋タイトル＋搭乗者名＋搭乗者姓　例）MR TARO SORANO, MR JIRO SORANO
            if (!!findTraveler && !!this.pnrInfo?.travelers) {
              const tmpTravelerName =
                (findTraveler.names?.[0]?.title ? findTraveler.names?.[0]?.title + ' ' : '') +
                (findTraveler.names?.[0]?.firstName ?? '') +
                ' ' +
                (findTraveler.names?.[0]?.lastName ?? '');
              if (travelerId === this.pnrInfo?.travelers[0]?.id) {
                travelerName = tmpTravelerName;
              } else {
                travelerName += ', ' + tmpTravelerName;
              }
            }
          });
          if (!!this.allSegmentInfo) {
            // 1SEG目の埋め込み文字列置換用情報 = {0}: 販売キャリアコード＋便番号＋搭乗者名
            // 2SEG目以降の埋め込み文字列置換用情報 = {0}: 埋め込み文字列置換用情報＋改行タグ＋販売キャリアコード＋便番号＋搭乗者名
            const tmpcombined = marketingAirlineCode + marketingFlightNumber + ' ' + travelerName;
            if (seg.flightId === this.allSegmentInfo[0]?.id) {
              combined = tmpcombined;
            } else {
              combined += '<br />' + tmpcombined;
            }
          }
        }
      }
    }
    return {
      contentHtml: warningCode,
      isCloseEnable: true,
      apiErrorCode: warning.code,
      errorMessageId: warningCode,
      interpolateParams: {
        0: combined,
      },
    };
  }

  /**
   * サービス情報登録APIが 異常終了の場合
   * @param error
   */
  private _handleUpdateServicesError(error: ApiErrorResponseModel) {
    const apiErrorCode = error?.['error'].errors?.at(0)?.code ?? '';
    const errorMessageId = this.ERROR_MAPPING[apiErrorCode];
    let message: RetryableError = { errorMsgId: errorMessageId };
    // エラーメッセージIDに"E0710"(チャイルドシートが申し込まれているにも関わらず、装着できない席を指定している旨)の場合、埋め込み文字を設定する。
    if (errorMessageId === 'E0710') {
      // 当該error.sourceからflightId、travelerIdを取得する。
      if (!!error?.['error'].errors?.at(0)) {
        const flightId = (error?.['error'].errors?.at(0)?.source as any)?.segmentId;
        const travelerId = (error?.['error'].errors?.at(0)?.source as any)?.travelerId;

        // 販売キャリアコードリストを空で作成する。
        let marketingAirlineCode = '';
        // 便番号リストを空で作成する。
        let marketingFlightNumber = '';
        // 搭乗者名リストを空で作成する。
        let travelerName = '';

        // セグメント情報が取得できた場合、販売キャリアコードと便番号を設定する。
        if (flightId) {
          const findSegment = this.allSegmentInfo?.find((seg) => seg.id === flightId);
          if (findSegment) {
            marketingAirlineCode = findSegment.marketingAirlineCode ?? '';
            marketingFlightNumber = findSegment.marketingFlightNumber ?? '';
          }
        }

        // 搭乗者情報が取得できた場合、搭乗者名を設定する。
        if (travelerId) {
          const findTraveler = this.pnrInfo?.travelers?.find((t) => t.id === travelerId);
          if (findTraveler) {
            travelerName =
              (findTraveler.names?.[0]?.title ? findTraveler.names?.[0]?.title + ' ' : '') +
              (findTraveler.names?.[0]?.firstName ?? '') +
              ' ' +
              (findTraveler.names?.[0]?.lastName ?? '');
          }
        }

        // 埋め込み文字列置換用情報 = {0}：ワーニングが発生したセグメントの販売キャリアコード＋便番号＋””＋エラーが発生した搭乗者名
        const combined = marketingAirlineCode + marketingFlightNumber + ' ' + travelerName;
        message = {
          errorMsgId: errorMessageId,
          apiErrorCode: apiErrorCode,
          params: [
            {
              key: 0,
              value: combined,
              dontTranslate: true,
            },
          ],
        };
      }
      // 画面のローディング処理の終了
      this._loadingSvc.endLoading();

      // 継続可能なエラー情報を指定し、当画面でエラーメッセージを表示する。本処理を中止とする。
      this._commonLibService.errorsHandlerService.setRetryableError(PageType.PAGE, message);
      this.close();

      // エラーメッセージIDに"E1828"(EMDの再利用対象の有料サービスに対して削除を指定)の場合、共通エラー画面に遷移しエラーメッセージを表示する。
    } else if (errorMessageId === 'E1828') {
      this._commonLibService.errorsHandlerService.setNotRetryableError({
        errorType: ErrorType.BUSINESS_LOGIC,
        apiErrorCode: apiErrorCode,
        errorMsgId: errorMessageId,
      });
      this.close();
    } else if (errorMessageId === 'E1814') {
      // 予約詳細画面で表示するためのエラーを設定
      this._servicingCommonService.updateServicingCommon({
        tabShow: this.tabShow,
        preErrorMessage: {
          errorType: ErrorType.BUSINESS_LOGIC,
          errorMsgId: errorMessageId,
          apiErrorCode: apiErrorCode,
        },
      });
      // 画面のローディング処理の終了
      this._loadingSvc.endLoading();
      this.close();
      this._router.navigate([SERVICING_ROUTES.myBooking]);
    } else {
      // 継続不可能エラータイプに”system”(システムエラー)にて継続不可能なエラー情報を指定し、共通エラー画面に遷移する。
      this._commonLibService.errorsHandlerService.setNotRetryableError({ errorType: ErrorType.SYSTEM });
      this.close();
    }
  }

  /**
   * Ancillary 支払い情報入力画面へ遷移する
   */
  moveToAncillaryPage() {
    let preWarningMessageList: Array<AlertMessageItem> = [];
    if (!!this._skipSegmentErrorMessageId) {
      preWarningMessageList.push({
        contentHtml: this._skipSegmentErrorMessageId,
        isCloseEnable: true,
        alertType: AlertType.WARNING,
        errorMessageId: this._skipSegmentErrorMessageId,
      });
    }

    // 4.1 Ancillary支払情報入力画面(S02-P020)渡すパラメータとして、以下を作成する。
    this._servicingCommonService.updateServicingCommon({
      initDisplayService: this.initDisplayService,
      tabShow: this.tabShow,
      seatReservationStatus: this.seatReservationStatus,
      preWarningMessageList: preWarningMessageList,
    });

    this.close();
    // 4.2	Ancillary支払情報入力画面(S02-P020)に遷移する。
    this._router.navigate([SERVICING_ROUTES.serviceRequestPaymentInput]);
  }

  /**
   * 他のサービス申し込むボタンのクリックイベント
   */
  onClickSelectOtherServices() {
    // 以下の確認ダイアログ表示処理を行う。
    if (this.showLastSegmentConfirmationDialog(false)) {
      return;
    }

    this.setOtherServicesInformation();
  }

  /**
   * 他のサービス申し込む情報指定
   */
  setOtherServicesInformation() {
    // 座席指定状況の設定
    this._setSeatReservationStatus();

    // セグメントスキップエラーメッセージIDが存在する場合、前画面からのワーニングメッセージリストに設定
    let preWarningMessageList: Array<AlertMessageItem> = [];
    if (!!this._skipSegmentErrorMessageId) {
      preWarningMessageList.push({
        contentHtml: this._skipSegmentErrorMessageId,
        isCloseEnable: true,
        alertType: AlertType.WARNING,
        errorMessageId: this._skipSegmentErrorMessageId,
      });
    }

    // 予約詳細(S01-P030)へ渡すパラメータとして、以下を作成する。
    this._servicingCommonService.updateServicingCommon({
      initDisplayService: this.initDisplayService,
      tabShow: this.tabShow,
      seatReservationStatus: this.seatReservationStatus,
      preWarningMessageList: preWarningMessageList,
    });

    // 2	指定状況詳細(PNR)モーダルを閉じる。
    this.close();
    // 予約詳細(S01-P030)へ遷移する。
    this._router.navigate([SERVICING_ROUTES.myBooking]);
  }

  /**
   * 最終セグメント確認ダイアログ表示処理
   */
  showLastSegmentConfirmationDialog(isApplyService: boolean) {
    // キャンセル済対象セグメント存在なし有料ASR席EMD有無=true、かつ有料ASR席・カウチ席新規申込有無=trueの場合、
    // ”MSG1020”(古い有料ASR席のEMDが払い戻しされる旨)
    if (
      this._servicingSeatmapService.servicingSeatmap.isTargetSegmentASREMDAvailable &&
      this._servicingSeatmapService.servicingSeatmap.isPaidASRCouchNewApplicationAvailable
    ) {
      this._confirmDialogs.push({
        size: DialogSize.L,
        type: DialogType.CHOICE,
        message: `${MasterJsonKeyPrefix.DYNAMIC}MSG1020`,
      });
    }

    // キャンセル済対象セグメント存在なしカウチ席EMD有無=true、かつ有料ASR席・カウチ席新規申込有無=trueの場合、
    // “MSG1110”(古いカウチ席のEMDが払い戻しされる旨)
    if (
      this._servicingSeatmapService.servicingSeatmap.isTargetSegmentCouchEMDAvailable &&
      this._servicingSeatmapService.servicingSeatmap.isPaidASRCouchNewApplicationAvailable
    ) {
      this._confirmDialogs.push({
        size: DialogSize.L,
        type: DialogType.CHOICE,
        message: `${MasterJsonKeyPrefix.DYNAMIC}MSG1110`,
      });
    }

    // 確認ダイアログ表示が必要な場合、確認ダイアログ表示
    if (this._confirmDialogs.length > 0) {
      // 全確認ダイアログで確定ボタンが押下された場合に後続処理を実施する
      this._showConfirmAgreement(() => {
        if (isApplyService) {
          this.setReservationInformation();
        } else {
          this.setOtherServicesInformation();
        }
      });
      return true;
    } else {
      return false;
    }
  }

  /**
   * 確認ダイアログ表示用配列の内容で、確認ダイアログを順次表示する。
   * 途中でキャンセルした場合は表示を中断する。
   * この処理を呼んだ時点で結果を問わず確認ダイアログ表示用配列は空になる。
   * @param onAllConfirm 全ダイアログで確定ボタン押下した際に実行する処理。省略可能。
   */
  private _showConfirmAgreement(onAllConfirm?: () => void) {
    // 確認ダイアログ表示用配列が存在するなら表示
    const dialog = this._confirmDialogs.shift();
    if (!!dialog) {
      const subscription = this._dialogDisplayService.openDialog(dialog).buttonClick$.subscribe((result) => {
        if (result.clickType === DialogClickType.CONFIRM) {
          // 確定押下時の処理

          if (this._confirmDialogs.length > 0) {
            // 次の確認ダイアログ表示処理を呼び出す
            this._showConfirmAgreement(onAllConfirm);
          } else {
            // これが最後の確認ダイアログであれば、全ダイアログで確定ボタン押下した際の処理を呼び出す
            if (!!onAllConfirm) onAllConfirm();
          }
        } else if (result.clickType === DialogClickType.CLOSE) {
          // キャンセル押下時の処理

          // キャンセル時は後続の確認ダイアログ表示を中断する
          this._confirmDialogs = [];
        }
        subscription.unsubscribe();
      });
    }
  }

  /**
   * 座席指定状況
   * @param passengerId 搭乗者ID
   * @param flightId セグメントID
   */
  getSeatNumber(passengerId: string, flightId: string | undefined) {
    let seatNumber = '';
    let tempSeat = this.selectedSeatInfoList?.find((s) => s.segmentId === flightId);
    let tempPassenger = tempSeat?.passengerList.find((p) => p.id === passengerId);
    let tempSeatNumber = tempPassenger?.seatNumberList?.find((s) => !!s.seatNumber);

    if (!!flightId) {
      if (tempSeat?.characteristicRequestSsrCode) {
        // 座席属性を選択中の場合
        seatNumber = this._seatAttributeDisplays.get(tempSeat?.characteristicRequestSsrCode) || '';
      }
      // カウチ座席を選択中の場合
      else if (this.isCouchSeat(passengerId, flightId)) {
        let couchSeatNumber = tempPassenger?.couchSeatNumberList?.find((s) => !!s.seatNumber);
        if (couchSeatNumber?.seatNumber) {
          seatNumber = couchSeatNumber?.seatNumber;
        }
      }
      // 有料席or無料席を選択中の場合
      else if (!!tempSeatNumber) {
        seatNumber = tempSeatNumber?.seatNumber;
      }
    }
    return seatNumber;
  }

  /**
   * シート金額
   * @param passengerId 搭乗者ID
   * @param flightId セグメントID
   * @returns
   */
  getSeatPrice(passengerId: string, flightId: string | undefined) {
    // 対象搭乗者
    const tempPassenger = this.selectedSeatInfoList
      ?.find((s) => s.segmentId === flightId)
      ?.passengerList.find((p) => p.id === passengerId);

    // 対象シートマップ情報
    const seatInfo = this.selectedSeatInfoList?.find((s) => s.segmentId === flightId);

    // 座席変更有無フラグ
    let isPassengerChange = false;

    // 座席属性の変更有無フラグ
    let isChangeCharacteristicRequestSsrCode = false;

    if (!!seatInfo && !!tempPassenger) {
      isPassengerChange = this._seatmapService.isPassengerChange(
        this._servicingSeatmapService.servicingSeatmap,
        seatInfo,
        tempPassenger,
        this.pnrInfo
      );

      isChangeCharacteristicRequestSsrCode = this._seatmapService.isChangedSeatAttribute(seatInfo);
    }

    if (!isChangeCharacteristicRequestSsrCode && isPassengerChange && !!tempPassenger?.specifiedAmount) {
      // 座席属性は未変更、かつ、座席を変更している、かつ、金額が存在する場合
      return tempPassenger.specifiedAmount;
    }

    return 0;
  }

  /**
   * 合計金額
   * @returns 金額
   */
  getTotalPrice(): number {
    let price: number = 0;
    this.allSegmentInfo?.forEach((segment) => {
      const seatInfo = this.selectedSeatInfoList?.find((s) => s.segmentId === segment.id);

      if (!!seatInfo) {
        let tempSeatNo: string[] = [];
        for (let passenger of seatInfo.passengerList) {
          // 該当搭乗者の座席に変更有無
          const isPassengerChange = this._seatmapService.isPassengerChange(
            this._servicingSeatmapService.servicingSeatmap,
            seatInfo,
            passenger,
            this.pnrInfo
          );

          // 座席属性の変更有無
          const isChangeCharacteristicRequestSsrCode = this._seatmapService.isChangedSeatAttribute(seatInfo);

          // 座席属性以外の変更がある場合
          if (isPassengerChange && !isChangeCharacteristicRequestSsrCode) {
            if (
              !!passenger?.couchSeatNumberList &&
              passenger?.couchSeatNumberList?.length > 0 &&
              passenger.couchSeatNumberList[0].seatNumber
            ) {
              let couchSeatNo = passenger.couchSeatNumberList[0].seatNumber;
              if (!tempSeatNo.includes(couchSeatNo)) {
                price += passenger.specifiedAmount || 0;
              }
              tempSeatNo.push(couchSeatNo);
            } else if (!!passenger) {
              price += passenger.specifiedAmount || 0;
            }
          }
        }
      }
    });
    return price;
  }

  /**
   * カウチ席の場合、搭乗者が表示終わりかどうかチェック
   * @param flightId セグメントID
   * @param passengerId 搭乗者ID
   */
  isAlreadyShown(flightId: string | undefined, passengerId: string) {
    let seatNo = this.getSeatNumber(passengerId, flightId);

    if (!!flightId && !!seatNo) {
      if (
        this.couchSeatInfo.find((couch) => couch.flightId === flightId) &&
        this.couchSeatInfo.find((couch) => couch.seatNo === seatNo)
      ) {
        return true;
      } else {
        this.couchSeatInfo.push({ flightId, seatNo });
        return false;
      }
    }

    return false;
  }

  /**
   * 同じカウチ席の搭乗者を取得
   * @param flightId セグメントID
   * @param passengerId 搭乗者ID
   */
  getSameCouchPassengers(flightId: string | undefined, passengerId: string) {
    let seatNo = this.getSeatNumber(passengerId, flightId);
    let passengerName = this.travelersMap?.get(passengerId)?.name;
    let couchPassengers: string[] = [];

    if (!!flightId && !!passengerId && !!seatNo) {
      if (!!passengerName) {
        couchPassengers.push(passengerName);
      }
      this.selectedSeatInfoList
        ?.find((s) => s.segmentId === flightId)
        ?.passengerList.forEach((p) => {
          if (
            !!p.couchSeatNumberList &&
            p.couchSeatNumberList?.length > 0 &&
            p.couchSeatNumberList[0].seatNumber === seatNo
          ) {
            if (p.id !== passengerId) {
              let tempPassenger = this.travelersMap?.get(p.id)?.name;
              if (!!tempPassenger) {
                couchPassengers.push(tempPassenger);
              }
            }
          }
        });
    }

    return couchPassengers;
  }

  /**
   * 席なし幼児名取得
   * @param passengerName 搭乗者名
   * @returns
   */
  getInfantName(passengerName?: string) {
    let infantName = '';

    if (!!passengerName) {
      this.travelersMap?.forEach((t) => {
        if (t.name === passengerName && !!t.accompanyingInfant) {
          infantName = t.accompanyingInfant.name;
        }
      });
    }

    return infantName;
  }

  /**
   * 操作オフィスPOSがメキシコかどうか
   */
  isMexicoOffice() {
    return this._commonLibService.aswContextStoreService.aswContextData.pointOfSaleId.includes('MEX');
  }

  /**
   * 対象座席情報に含まれる指定搭乗者に対して、座席を変更しているかどうか判定する
   * @param seatInfo  対象座席情報
   * @param passengerId 指定搭乗者（搭乗者ID）
   * @returns true：対象搭乗者は座席変更中、false：未変更
   */
  private _isPassengerChangedSeat(seatInfo: SeatInfo, passengerId: string): boolean {
    // 同ファイル内の「getSegmentChangeList」の変更判定に準拠する
    let isPassengerChangedSeat = false;
    let passenger: SeatInfoPassengers | undefined = seatInfo.passengerList.find((p) => p.id === passengerId);
    if (!!passenger) {
      let pnrSeat = !!this.pnrInfo?.seats?.[seatInfo.segmentId]?.[passenger.id]
        ? this.pnrInfo?.seats?.[seatInfo.segmentId]?.[passenger.id].seatSelection.seatNumbers
        : undefined;
      let pnrCouchSeat = !!this.pnrInfo?.seats?.[seatInfo.segmentId]?.[passenger.id]
        ? this.pnrInfo?.seats?.[seatInfo.segmentId]?.[passenger.id].seatSelection.couchSeatNumber
        : undefined;

      // 座席属性の変更有無フラグ
      const isChangeCharacteristicRequestSsrCode = this._seatmapService.isChangedSeatAttribute(seatInfo);

      if (pnrSeat && pnrSeat.length > 0) {
        // 座席情報の取得
        let seatInformation = this._seatmapService.getSeatInformation(
          seatInfo.segmentId,
          pnrSeat[0],
          this._servicingSeatmapService.servicingSeatmap
        );

        // 無料席で座席の変更がある場合、表示セグメントに追加する
        let isFreeSSRSeat = !seatInformation?.isCouchSeat && !seatInformation?.isChargeableAsrSeat;

        // seatNumberList.seatNumber が空欄（無料席取消）の場合、表示セグメントに追加する
        if (isFreeSSRSeat && passenger.seatNumberList?.length === 0) {
          isPassengerChangedSeat = true;
        }
        // SSR席(カウチ席)を変更する場合、表示セグメントに追加する
        else if (
          passenger.couchSeatNumberList &&
          passenger.couchSeatNumberList.length > 0 &&
          pnrCouchSeat !== passenger.couchSeatNumberList[0].seatNumber
        ) {
          isPassengerChangedSeat = true;
        }
        // SSR席(有料席)を変更する場合、表示セグメントに追加する
        else if (
          !pnrCouchSeat &&
          passenger.seatNumberList &&
          passenger.seatNumberList?.length > 0 &&
          pnrSeat[0] !== passenger.seatNumberList[0].seatNumber
        ) {
          isPassengerChangedSeat = true;
        }
        // SSR登録済み（有料）座席の取消を表示セグメントに追加
        else if (passenger.seatNumberList?.length === 0 && passenger.couchSeatNumberList?.length === 0) {
          isPassengerChangedSeat = true;
        }
        // 座席属性指定の場合、セグメントに追加
        else if (isChangeCharacteristicRequestSsrCode) {
          // SSR登録済みセグで座席属性指定画面が表示され、属性指定した場合のフロー
          isPassengerChangedSeat = true;
        }
      }
      // 新規登録の座席を表示セグメントに追加
      else if (
        (passenger.seatNumberList && passenger.seatNumberList?.length > 0) ||
        (passenger.couchSeatNumberList &&
          passenger.couchSeatNumberList?.length > 0 &&
          passenger.couchSeatNumberList[0].seatNumber)
      ) {
        isPassengerChangedSeat = true;
      }
      // 座席属性指定の場合、セグメントに追加
      else if (isChangeCharacteristicRequestSsrCode) {
        // 座席属性が未登録or登録済み、かつ、座席属性が変更された場合のフロー
        isPassengerChangedSeat = true;
      }
    }
    return isPassengerChangedSeat;
  }

  /**
   * カウチ座席の着席順を補正した座席番号を返却する
   * @param couchSeatNumber 対象のカウチ座席番号
   * @param passengerApplicationStatusMap 座席指定状況
   * @param seatInformationMap 座席情報
   * @returns 補正できた場合：補正した座席番号、補正できない場合：空文字
   */
  private _getAdjusttedSeatNumber(
    couchSeatNumber: SeatNumbers[],
    passengerApplicationStatusMap: Map<string, PassengerApplicationStatusMap>,
    seatInformationMap?: { [seatId: string]: SeatForServicingSeatmapScreen }
  ): string {
    // シートマップ情報から該当のカウチ座席を取得して判定
    // 通路側（複数あれば左を優先）から順に確認して、空いている座席を設定
    // 例）「70ABC、通路、70DEFG、通路、70HJK」の場合、「70C ⇒ 70B ⇒ 70A」「70D ⇒ 70E ⇒ 70F ⇒ 70G」「70H ⇒ 70J ⇒ 70K」

    // 補正した座席番号。補正できなかった（座席が決められない）場合は空文字
    let adjusttedSeatNumber = '';

    // 対象の搭乗者に設定されているカウチ座席番号から、行番号・座席番号の英字部分の配列を作る
    const splitCouchNumber = splitCouchSeatNumber(couchSeatNumber[0].seatNumber);

    // 各判定に必要な内容が存在する場合に後続処理を実行
    if (
      !!seatInformationMap &&
      !!splitCouchNumber &&
      !!splitCouchNumber.rowNumber &&
      !!splitCouchNumber.columnNumbers &&
      splitCouchNumber.columnNumbers?.length > 0
    ) {
      // 行番号
      const splitCoucRowNumber: string = splitCouchNumber.rowNumber;

      // 列番号：英字順にソート（ABCのような順番になる）
      splitCouchNumber.columnNumbers.sort();

      // 配列の先頭に該当する座席の属性が「通路側」かどうかチェックする
      // 通路側では無い場合、配列の末尾が「通路側」であるものとして、列番号を逆順ソートする（CBAのような順になる）
      let firstSeatNumberOfTargetCouch = [splitCoucRowNumber, splitCouchNumber.columnNumbers[0]].join('');
      let isAisleSide = !!seatInformationMap[firstSeatNumberOfTargetCouch].seatCharacteristicsCodes?.find(
        (c) => c === 'A'
      );
      if (!isAisleSide) splitCouchNumber.columnNumbers.reverse();

      // 申し込み予定の座席番号（値が設定されているものだけ）を配列にする
      const applicationSeatNumbers = Array.from(passengerApplicationStatusMap.values())
        .map((m) => (!!m.seatNumber && m.seatNumber?.length > 0 ? m.seatNumber[0].seatNumber : ''))
        .filter((n) => !!n);

      // 対象のカウチ座席で空いている座席を先頭から順に探して設定する（申し込み予定の座席番号に含まれない、最初の値を使う）
      let adjusttedColNumber = splitCouchNumber.columnNumbers.find(
        (c) => !applicationSeatNumbers.includes([splitCoucRowNumber, c].join(''))
      );
      adjusttedSeatNumber = !!adjusttedColNumber ? [splitCoucRowNumber, adjusttedColNumber].join('') : '';
    }
    return adjusttedSeatNumber;
  }

  /**
   * 指定したセグメントID・搭乗者IDに対応する、選択中の座席が有料ASRかどうか
   * @param passengerId 対象搭乗者
   * @param segmentId 対象セグメント
   * @returns true：有料ASR、false：有料ASR以外（判定不可の場合も含む）
   */
  isChargeableAsrSeat(passengerId: string, segmentId: string | undefined): boolean {
    // 有料ASRかどうか判定フラグを返却する（判定できない場合はfalse）
    // 座席属性が同時に指定されている状況もあり得るので、その考慮も必要
    const seatInfo = this.selectedSeatInfoList?.find((s) => s.segmentId === segmentId);
    const isChangeCharacteristicRequestSsrCode: boolean = !!seatInfo
      ? this._seatmapService.isChangedSeatAttribute(seatInfo)
      : false;
    const isChargeableAsrSeat: boolean =
      this._getSeatInformationByPassengerId(passengerId, segmentId)?.isChargeableAsrSeat || false;

    // 「座席属性が未定、かつ、有料ASR」の場合、有料ASRである扱いとする
    return !isChangeCharacteristicRequestSsrCode && isChargeableAsrSeat;
  }

  /**
   * 指定したセグメントID・搭乗者IDに対応する、選択中の座席がカウチ座席かどうか
   * @param passengerId 対象搭乗者
   * @param segmentId 対象セグメント
   * @returns true：カウチ座席、false：カウチ座席以外（判定不可の場合も含む）
   */
  isCouchSeat(passengerId: string, segmentId: string | undefined): boolean {
    // 座席情報のカウチ判定フラグを返却する（判定できない場合はfalse）
    // 座席属性が同時に指定されている状況もあり得るので、その考慮も必要
    const seatInfo = this.selectedSeatInfoList?.find((s) => s.segmentId === segmentId);
    const isChangeCharacteristicRequestSsrCode: boolean = !!seatInfo
      ? this._seatmapService.isChangedSeatAttribute(seatInfo)
      : false;
    const isCouchSeat: boolean = this._getSeatInformationByPassengerId(passengerId, segmentId)?.isCouchSeat || false;

    // 「座席属性が未定、かつ、カウチ座席」の場合、カウチ座席である扱いとする
    return !isChangeCharacteristicRequestSsrCode && isCouchSeat;
  }

  /**
   * 指定したセグメントID・搭乗者IDに対応する、選択中の座席情報を取得
   * @param passengerId 対象搭乗者
   * @param segmentId 対象セグメント
   * @returns 取得できた場合：座席情報、取得できなかった場合：undefined
   */
  private _getSeatInformationByPassengerId(
    passengerId: string,
    segmentId: string | undefined
  ): SeatForServicingSeatmapScreen | undefined {
    // 画面選択時の座席番号に一致する、APIで取得した座席情報を取得します
    // カウチ座席の場合、参照したい情報の特性に応じて呼び出し側で考慮が必要です
    if (!!segmentId) {
      let targetSegment = this.selectedSeatInfoList?.find((s) => s.segmentId === segmentId);
      let targetPassenger = targetSegment?.passengerList.find((p) => p.id === passengerId);
      if (!!targetPassenger && !!targetPassenger.seatNumberList && targetPassenger.seatNumberList.length > 0) {
        return this._seatmapService.getSeatInformation(
          segmentId,
          targetPassenger.seatNumberList[0].seatNumber,
          this._servicingSeatmapService.servicingSeatmap
        );
      }
    }
    return undefined;
  }

  /**
   * サービシングフロー判定
   * @returns true：サービシングフロー、false：サービシングフロー以外
   */
  isServicingFlow() {
    return (
      this._commonLibService.aswCommonStoreService.getFunctionId(PageType.PAGE) === ServicingFunctionIdType.SEATMAP
    );
  }

  /**
   * 内容確定・購入確認文言ラベルで使う静的文言keyを取得する
   */
  getConfirmLabelStaticMsgKey(): string {
    // keyの初期値は「座席を確定する旨」とする
    let msgKey: string = 'm_static_message-label.confirmToApplySeats';

    // 有料ASR席、またはカウチ席のいずれかを指定している場合、座席の購入手続きへ進む旨
    let segments = this._seatmapService.getSegmentChangeList(
      this._servicingSeatmapService.servicingSeatmap,
      this.allSegmentInfo,
      this.selectedSeatInfoList,
      this.pnrInfo
    );
    for (const segment of segments) {
      // 変更されているセグメントをループ処理
      let existsChargeableAsrSeatOrCouchSeat = this.passengerIdList.some((passengerId) => {
        // 対象セグ・搭乗者の座席が「有料ASR席 or カウチ席」であれば、trueが返却される。
        return this.isChargeableAsrSeat(passengerId, segment.id) || this.isCouchSeat(passengerId, segment.id);
      });

      if (existsChargeableAsrSeatOrCouchSeat) {
        // 「有料ASR席 or カウチ席」が1つでも含まれる場合
        msgKey = 'm_static_message-label.ConfirmToPurchaseSeats';
        break;
      }
    }

    // 上記以外の場合、座席を確定する旨
    return msgKey;
  }

  /**
   * 内容確定・購入へボタンで使う静的文言keyを取得する
   */
  getApplyButtonStaticMsgKey(): string {
    // keyの初期値は「確定する旨」とする
    let msgKey: string = 'm_static_message-label.registerSeat';

    // 判定で使う合計金額
    const totalPrice: number = this.getTotalPrice();
    if (totalPrice >= 1) {
      // 1以上の場合、ボタン名を購入へ進む旨とする。
      msgKey = 'm_static_message-label.toPurchase';
    }

    // それ以外の場合、ボタン名の内容を確定する旨とする。
    return msgKey;
  }

  /**
   * 変更があるセグメントを取得する
   * @returns 変更があるセグメント
   */
  getSegmentChangeList() {
    return this._seatmapService.getSegmentChangeList(
      this._servicingSeatmapService.servicingSeatmap,
      this.allSegmentInfo,
      this.selectedSeatInfoList,
      this.pnrInfo
    );
  }

  /** 表示対象搭乗者IDリストを取得
   * @param segmentId セグメントID
   * @returns 表示対象搭乗者IDリスト
   */
  getDisplayPassengerList(segmentId: string | undefined): string[] {
    // 表示対象搭乗者IDリスト
    let displayPassengerList: string[] = [];

    // セグメントIDが存在しない場合、空のリストを返す。
    if (!segmentId) {
      return displayPassengerList;
    }
    this.passengerIdList.forEach((passengerId) => {
      const seatInfo = this.selectedSeatInfoList?.find((s) => s.segmentId === segmentId);
      if (!!seatInfo) {
        const passenger = seatInfo.passengerList?.find((p) => p.id === passengerId);
        if (!!passenger) {
          // 当該搭乗者の座席に変更がある場合、搭乗者IDを表示対象搭乗者IDリストに追加する
          if (
            this._seatmapService.isPassengerChange(
              this._servicingSeatmapService.servicingSeatmap,
              seatInfo,
              passenger,
              this.pnrInfo
            )
          ) {
            displayPassengerList.push(passengerId);
          }
        }
      }
    });

    return displayPassengerList;
  }
}
